import React, { useEffect, useState } from "react";
import {
  adminApi,
  type PlaylistSyncSettings,
  type SyncCadence,
} from "../../../repositories/admin";
import Button from "../../../components/ui/Button";
import {
  formInputClass,
  formLabelClass,
  formSelectClass,
  formHelpClass,
  formCheckboxClass,
} from "../../../components/ui/formStyles";

/**
 * The AD-305 sync settings an admin can change without a deploy: the one-time backfill,
 * how often records and roster artists are synced, and when a placement counts as stale.
 * Each is a row in app_settings that the jobs read on their next run.
 *
 * The backfill estimate sits beside the switch because the ticket asks for it to be read
 * before the backfill is turned on.
 */

const PITCH_CADENCE_LABELS: Record<SyncCadence, string> = {
  daily: "Daily",
  weekly: "Weekly",
  fortnightly: "Every two weeks",
  off: "Off",
};

const ROSTER_CADENCE_LABELS: Record<SyncCadence, string> = {
  daily: "Daily",
  weekly: "Weekly (Fridays)",
  fortnightly: "Every other Friday",
  off: "Off",
};

const CADENCES: SyncCadence[] = ["daily", "weekly", "fortnightly", "off"];

interface FormState {
  backfillEnabled: boolean;
  dailyCallBudget: string;
  recordsPerTick: string;
  pitchCadence: SyncCadence;
  rosterCadence: SyncCadence;
  staleAfterDays: string;
}

const toForm = (settings: PlaylistSyncSettings): FormState => ({
  backfillEnabled: settings.backfill.enabled,
  dailyCallBudget: String(settings.backfill.dailyCallBudget),
  recordsPerTick: String(settings.backfill.recordsPerTick),
  pitchCadence: settings.cadence.pitch,
  rosterCadence: settings.cadence.roster,
  staleAfterDays: String(settings.staleAfterDays),
});

const number = (value: number) => value.toLocaleString();

export const PlaylistSyncSettingsCard: React.FC = () => {
  const [settings, setSettings] = useState<PlaylistSyncSettings | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const apply = (next: PlaylistSyncSettings) => {
    setSettings(next);
    setForm(toForm(next));
  };

  useEffect(() => {
    adminApi
      .getPlaylistSyncSettings()
      .then((res) => apply(res.data.data))
      .catch(() => setError("Could not load the playlist sync settings."));
  }, []);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setSaved(false);
    setForm((current) => (current ? { ...current, [key]: value } : current));
  };

  const save = async () => {
    if (!form || !settings) return;

    if (form.backfillEnabled && !settings.backfill.enabled) {
      const { estimate } = settings;
      const question =
        `Switch the backfill on? About ${number(estimate.estimatedCalls)} SoundCharts calls for ` +
        `${number(estimate.records)} records, spread over about ${number(estimate.estimatedDays)} day(s).`;
      if (!window.confirm(question)) return;
    }

    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await adminApi.updatePlaylistSyncSettings({
        backfill: {
          enabled: form.backfillEnabled,
          dailyCallBudget: Number(form.dailyCallBudget),
          recordsPerTick: Number(form.recordsPerTick),
        },
        cadence: { pitch: form.pitchCadence, roster: form.rosterCadence },
        staleAfterDays: Number(form.staleAfterDays),
      });
      apply(res.data.data);
      setSaved(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || "The changes were not saved. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!settings || !form) {
    return (
      <div className="p-4 mb-6 bg-white border rounded-lg border-neutral-200">
        <p className="text-xs text-neutral-500 sm:text-sm">Playlist sync settings</p>
        {error ? (
          <p className="mt-2 text-sm font-medium text-red-600">{error}</p>
        ) : (
          <p className="mt-1 text-lg font-bold text-black">…</p>
        )}
      </div>
    );
  }

  const { estimate } = settings;

  return (
    <div className="p-4 mb-6 bg-white border rounded-lg border-neutral-200">
      <p className="text-xs text-neutral-500 sm:text-sm">Playlist sync settings</p>

      {!settings.syncEnabled && (
        <p className="mt-2 text-sm text-amber-700">
          Playlist sync is not running on this server. These settings are saved, but nothing
          uses them until the sync is switched on.
        </p>
      )}

      {/* Backfill */}
      <div className="mt-4">
        <h4 className="text-sm font-semibold text-black">Backfill of earlier records</h4>
        <p className={formHelpClass}>
          Pulls the full playlist history of records pitched before playlist sync shipped, a few
          at a time, inside a daily call budget. Records already pulled are never pulled again.
        </p>

        <div className="grid gap-3 mt-3 sm:grid-cols-4">
          <div className="p-3 rounded-md bg-neutral-50">
            <p className="text-xs text-neutral-500">Records still to backfill</p>
            <p className="mt-1 text-lg font-bold text-black">{number(estimate.records)}</p>
            {estimate.withoutIsrc > 0 && (
              <p className="text-xs text-amber-700">
                {number(estimate.withoutIsrc)} have no ISRC and will fail
              </p>
            )}
          </div>
          <div className="p-3 rounded-md bg-neutral-50">
            <p className="text-xs text-neutral-500">Estimated calls</p>
            <p className="mt-1 text-lg font-bold text-black">{number(estimate.estimatedCalls)}</p>
            <p className="text-xs text-neutral-500">
              {number(estimate.callsPerRecord)} per record,{" "}
              {estimate.callsPerRecordMeasured
                ? `measured over ${number(estimate.sampledRecords)} record(s)`
                : "estimated"}
            </p>
          </div>
          <div className="p-3 rounded-md bg-neutral-50">
            <p className="text-xs text-neutral-500">Fits in this month's quota</p>
            <p
              className={`mt-1 text-lg font-bold ${
                estimate.fitsInRemainingQuota ? "text-black" : "text-red-600"
              }`}
            >
              {estimate.fitsInRemainingQuota ? "Yes" : "No"}
            </p>
            <p className="text-xs text-neutral-500">
              {number(estimate.remainingQuota)} calls remaining
            </p>
          </div>
          <div className="p-3 rounded-md bg-neutral-50">
            <p className="text-xs text-neutral-500">Estimated days to finish</p>
            <p className="mt-1 text-lg font-bold text-black">{number(estimate.estimatedDays)}</p>
            <p className="text-xs text-neutral-500">{number(estimate.pulledSoFar)} pulled so far</p>
          </div>
        </div>

        <div className="grid gap-4 mt-4 sm:grid-cols-3">
          <label className="flex items-center gap-2 text-sm font-medium text-neutral-700">
            <input
              type="checkbox"
              className={formCheckboxClass}
              checked={form.backfillEnabled}
              onChange={(e) => update("backfillEnabled", e.target.checked)}
            />
            Backfill on
          </label>
          <div>
            <label htmlFor="backfill-daily-budget" className={formLabelClass}>
              Daily call budget
            </label>
            <input
              id="backfill-daily-budget"
              type="number"
              min={1}
              className={formInputClass}
              value={form.dailyCallBudget}
              onChange={(e) => update("dailyCallBudget", e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="backfill-records-per-tick" className={formLabelClass}>
              Records per 15-minute run
            </label>
            <input
              id="backfill-records-per-tick"
              type="number"
              min={1}
              max={20}
              className={formInputClass}
              value={form.recordsPerTick}
              onChange={(e) => update("recordsPerTick", e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Cadence and staleness */}
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-black">Sync schedule</h4>
        <div className="grid gap-4 mt-3 sm:grid-cols-3">
          <div>
            <label htmlFor="pitch-cadence" className={formLabelClass}>
              Pitched records
            </label>
            <select
              id="pitch-cadence"
              className={formSelectClass}
              value={form.pitchCadence}
              onChange={(e) => update("pitchCadence", e.target.value as SyncCadence)}
            >
              {CADENCES.map((cadence) => (
                <option key={cadence} value={cadence}>
                  {PITCH_CADENCE_LABELS[cadence]}
                </option>
              ))}
            </select>
            {/* The first pull is what this cadence cannot switch off. It still needs the sync
                running, which the warning above covers; saying "either way" alone read as if
                history arrived even with the sync off. */}
            <p className={formHelpClass}>
              How often pulled records are re-checked. New pitches still get their first
              pull, even when this is off.
            </p>
          </div>
          <div>
            <label htmlFor="roster-cadence" className={formLabelClass}>
              Roster artists
            </label>
            <select
              id="roster-cadence"
              className={formSelectClass}
              value={form.rosterCadence}
              onChange={(e) => update("rosterCadence", e.target.value as SyncCadence)}
            >
              {CADENCES.map((cadence) => (
                <option key={cadence} value={cadence}>
                  {ROSTER_CADENCE_LABELS[cadence]}
                </option>
              ))}
            </select>
            <p className={formHelpClass}>Runs at 06:00 Pacific.</p>
          </div>
          <div>
            <label htmlFor="stale-after-days" className={formLabelClass}>
              Stale after (days)
            </label>
            <input
              id="stale-after-days"
              type="number"
              min={1}
              max={365}
              className={formInputClass}
              value={form.staleAfterDays}
              onChange={(e) => update("staleAfterDays", e.target.value)}
            />
            <p className={formHelpClass}>
              Placements SoundCharts has not confirmed for this long are listed but not counted.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-6">
        <Button loading={saving} disabled={saving} onClick={save}>
          Save settings
        </Button>
        {saved && <p className="text-sm text-neutral-600">Saved. The jobs use it from their next run.</p>}
        {error && <p className="text-sm font-medium text-red-600">{error}</p>}
      </div>
    </div>
  );
};

export default PlaylistSyncSettingsCard;
